type Resolvable<R> = R | PromiseLike<R>;

type IterateFunction<T, R> = (item: T, index: number) => Resolvable<R>;

/**
 * Map an array (or any iterable) which contains a list of promises (or a mix of promises and values) with the given `mapper` function with the signature `(item, index)`
 * where `item` is the resolved value of a respective promise in the input array.
 * If any promise in the input array is rejected the returned promise is rejected as well.
 *
 * If the `mapper` function returns promises or thenables, the returned promise will wait for all the mapped results to be resolved as well.
 *
 * `options.concurrency` represents the maximum amount of promises that can run at a given time. This prevents OOM errors for very large lists of `values` by only allowing
 * `options.concurrency` concurrent `mapper` functions to execute simultaneously. The minimum allowed `options.concurrency` is 1
 *
 * *The original array is not modified.*
 *
 * This is a combo of the `Bluebird.map` function and this implementation: https://betterprogramming.pub/implement-your-own-bluebird-style-promise-map-in-js-7c081b7ad02c
 *
 */
export default async function map<R, U>(
  values: Iterable<Resolvable<R>>,
  mapper: IterateFunction<R, U>,
  options: { concurrency: number },
): Promise<U[]> {
  // do not allow a concurrency less than 1
  let concurrencyCt = options.concurrency < 1 ? 1 : options.concurrency;
  let index = 0;
  const results: U[] = [];
  const iterator = values[Symbol.iterator]();
  const workerPromises: Array<Promise<null>> = [];

  while (concurrencyCt-- > 0) {
    const promise = promiseWrappedMapper();
    if (promise) {
      workerPromises.push(promise);
    } else {
      break;
    }
  }

  // promises will be the size of `concurrency`, so only `concurrency` concurrent promises can be executed
  await Promise.all(workerPromises);
  return results;

  // NOTE that this is intentionally not `async`, since it may return a `null` value (not wrapped in a Promise)
  // This function is scoped inside the `map` function as it accesses outer scope variables directly
  function promiseWrappedMapper(): null | Promise<null> {
    const next = iterator.next();
    if (next.done) {
      return null;
    }

    const i = index++;
    // next.value could be a Promise, so we wrap in Promise.resolve to ensure it gets executed properly
    return Promise.resolve(next.value).then(val => {
      const mapped = mapper(val, i);
      return Promise.resolve(mapped).then(resolved => {
        results[i] = resolved;
        return promiseWrappedMapper();
      });
    });
  }
}
